import { DocumentContact } from '@mercator-gcp/types/backend/DocumentContact';
import { CompanyItem } from 'types';
import { REGISTERED_ACCESSIBILITY_SPECIALIST_APPLICANT_TITLE } from 'utils/constants';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import type { InvolvedResponse } from 'hooks/useCompanies';

/**
 * Joins sequences of single-letter tokens that are separated by spaces, dots, or hyphens
 * into a single word, while preserving longer tokens as-is.
 *
 * Example:
 *   "C . N . N  News" → "CNN News"
 *   "B B C - Radio"  → "BBC Radio"
 *
 * Rules:
 * - Single-character tokens (e.g. "C", ".", "N", "-") are grouped together.
 * - Separators like space, dot (.), and hyphen (-) are removed during joining.
 * - Non-single-letter words are kept separate.
 * - Extra spaces are removed from the final result.
 *
 * @param text - The input string to process.
 * @returns A cleaned and normalized string with characters properly joined.
 */
const joinCharsWithSymbols = (text: string): string => {
  const tokens = text.trim().split(/\s+/);
  if (tokens.length === 1) return text.trim();

  const joinedTokens: string[] = [];
  let buffer: string[] = [];

  tokens.forEach((token) => {
    const cleaned = token.replace(/[\s.-]/g, '');

    if (cleaned.length === 1) {
      buffer.push(cleaned);
    } else {
      if (buffer.length) {
        joinedTokens.push(buffer.join(''));
        buffer = [];
      }
      joinedTokens.push(cleaned);
    }
  });

  if (buffer.length) {
    joinedTokens.push(buffer.join(''));
  }

  return joinedTokens.join(' ').replace(/\s+/g, ' ').trim();
};

const stripTrailingNumbers = (text: string): string => {
  // Do not strip if only two words and no '#'
  if ((text.match(/ /g) || []).length === 1 && !text.includes('#')) {
    return text.trim();
  }

  // Make sure '#' is treated as a separate token
  const normalizedText = text.replace(/#/g, ' #');

  // Pattern: trailing special character(s) + optional space + digits + optional letter
  const trailingPattern = /\s([^\w\s]+ ?\d+[A-Za-z]?)$/;
  const match = normalizedText.match(trailingPattern);

  const stripped = match ? normalizedText.replace(match[0], '') : normalizedText;

  return stripped.trim();
};

const removeMultiSpaces = (text: string): string => {
  // replace multiple spaces with a single space
  return text.replace(/\s+/g, ' ');
};

const stripStartOrEndSymbols = (text: string): string => {
  // remove characters matching the pattern at the start or end of the string
  // replace asterisks and pipes with spaces
  return text
    .replace(/^[^0-9A-Za-z'&+:#]+|[^0-9A-Za-z'&+:#]+$/g, '')
    .replace(/[*|]/g, ' ');
};

const removeMiscNoise = (text: string): string => {
  // remove specific asterisk pattern
  return text.replace(/\*\s?main$|\smain\*|\*\s?main\s?\*/g, '').replace('company:', '');
};

/**
 * Strips symbols and optionally removes stopwords from a space-delimited string.
 *
 * - Converts the text to lowercase and splits it by spaces.
 * - Skips any token matched by `skipTokenIfMatched`.
 * - Strips symbols from other tokens using `stripSymbols`.
 *
 * @param text - The input string to clean.
 * @returns A cleaned and normalized string.
 */
const stripByTokens = (text: string): string => {
  return text
    .toLowerCase()
    .split(' ')
    .map((token) => token.trim())
    .map((token) => {
      if (/[A-Za-z]\/[A-Za-z]/.test(token)) {
        return token;
      }

      return stripStartOrEndSymbols(token);
    })
    .filter((token) => !!token)
    .join(' ');
};

export const cleanName = (name: string | null | undefined): string => {
  /**
   * Todo: this function mirrors logic that exists in the company alias pipeline.
   * Ideally, this logic should be deduplicated and we should be able to get the company information from
   * the backend directly with the related contacts. This will require changes to the company alias pipeline
   * as well as the backend/frontend contract.
   * https://github.com/Mercator-AI/mercator-lib-task-code/blob/db76984cb9d73a66bde9e6544b4ecc9af74ffc03/task_code/process_companyaliases/big_query/utils/utils_name_clean.py#L300
   */
  if (!name) return '';

  let cleanedName = name.trim();

  const chainFunctions = [
    removeMiscNoise,
    stripByTokens,
    stripStartOrEndSymbols,
    stripTrailingNumbers,
    removeMultiSpaces,
    joinCharsWithSymbols,
  ];

  chainFunctions.forEach((func) => {
    if (!cleanedName || /^\d+$/.test(cleanedName)) return '';

    cleanedName = func(cleanedName);

    return cleanedName;
  });

  return cleanedName?.trim().toLowerCase() || '';
};

export const sortCompanies = (companies: CompanyItem[]) => {
  return companies.sort((a, b) => {
    if (a.uuid && !b.uuid) return -1;
    if (!a.uuid && b.uuid) return 1;

    return (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' });
  });
};

export const combineCompaniesAndRelatedCompanies = (
  relatedContacts: DocumentContact[] | string,
  companyList: CompanyItem[] | InvolvedResponse,
) => {
  // convert everything to CompanyItem to avoid issues
  const combinedCompanyList: CompanyItem[] = (companyList as Array<CompanyItem | any>)
    .filter((company) => !!company.name)
    .map((company) => {
      return {
        uuid: company.uuid,
        name: company.name,
        aliases: company.aliases ?? [],
      } as CompanyItem;
    });

  parseRelatedContactsIntoList(relatedContacts).forEach((contact) => {
    const cleanContactName = cleanName(contact?.name || contact?.company);

    // skip nameless items
    if (!cleanContactName) return;

    // Check if the contact is already in the list
    const companyIndex = combinedCompanyList.findIndex(
      (company) =>
        company.name.trim().toLowerCase() === cleanContactName ||
        company.aliases?.map((alias) => cleanName(alias)).includes(cleanContactName),
    );

    // If it's not in the list, add it
    if (companyIndex === -1) {
      combinedCompanyList.push({
        uuid: '',
        name: cleanContactName,
        aliases: [],
      });
    } else if (contact.type === REGISTERED_ACCESSIBILITY_SPECIALIST_APPLICANT_TITLE) {
      // Remove registered accessibility specialists from the companies list if they're there
      combinedCompanyList.splice(companyIndex, 1);
    }
  });

  // put items with a uuid at the beginning, then sort by name
  return sortCompanies(combinedCompanyList);
};
